import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from '../components/BreadCrumbs';
import { getAIUserInbox } from '../services/aiChat';
import { formatDate } from '../utils/FormatDateTime';
import { CustomCard } from '../components/cards/CustomCard';
import Button from '../components/buttons/Button';
import { MessageSquare, Calendar, ChevronRight } from 'lucide-react';

const AIUserChats = () => {
    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const navigate = useNavigate();

    useEffect(() => {
        fetchChats();
    }, [page]);

    const fetchChats = async () => {
        try {
            setLoading(true);
            const response = await getAIUserInbox(page);
            setChats(response.data.results);
            setTotalPages(response.data.total_pages);
        } catch (error) {
            console.error('Error fetching AI user chats:', error);
            toast.error('Failed to fetch AI user chats');
        } finally {
            setLoading(false);
        }
    };

    const handleChatClick = (userId) => {
        navigate(`/ai-user-chats/${userId}`);
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <Breadcrumbs items={[{ name: 'Home', link: '/dashboard' }, { name: 'AI User Chats' }]} />
                <div className="flex items-center gap-3 mt-3">
                    <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg text-white">
                        <MessageSquare className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">AI User Chats</h1>
                        <p className="text-gray-600 mt-1">View conversations between users and AI Assistant</p>
                    </div>
                </div>
            </div>

            <CustomCard>
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                ) : (
                    <div className="overflow-hidden">
                        <div className="grid grid-cols-1 divide-y divide-gray-100">
                            {chats.map((chat) => (
                                <div
                                    key={chat.user_id}
                                    onClick={() => handleChatClick(chat.user_id)}
                                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between group"
                                >
                                    <div className="flex items-center gap-4 flex-1 min-w-0">
                                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 font-bold shrink-0">
                                            {chat.user_details?.first_name ? chat.user_details.first_name[0] : 'U'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-semibold text-gray-900 truncate">
                                                    {chat.user_details?.first_name ? `${chat.user_details.first_name} ${chat.user_details.last_name || ''}` : `User ${chat.user_id.substring(0, 8)}...`}
                                                </h3>
                                                {chat.user_details?.mobile_number && (
                                                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                                        {chat.user_details.mobile_number}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-600 truncate mt-1 italic">
                                                "{chat.preview_message}"
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 ml-4">
                                        <div className="text-right hidden sm:block">
                                            <div className="flex items-center gap-1 text-xs text-gray-500">
                                                <Calendar className="w-3 h-3" />
                                                <span>{formatDate(chat.last_active)}</span>
                                            </div>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-primary transition-colors" />
                                    </div>
                                </div>
                            ))}
                            {chats.length === 0 && (
                                <div className="text-center py-12 text-gray-500">
                                    No chat records found.
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center gap-2 p-4 pt-6 border-t border-gray-100">
                                <Button
                                    disabled={page === 1}
                                    onClick={() => setPage(page - 1)}
                                    className="px-4 py-2 border rounded-lg disabled:opacity-50"
                                >
                                    Previous
                                </Button>
                                <span className="text-sm text-gray-600">
                                    Page {page} of {totalPages}
                                </span>
                                <Button
                                    disabled={page === totalPages}
                                    onClick={() => setPage(page + 1)}
                                    className="px-4 py-2 border rounded-lg disabled:opacity-50"
                                >
                                    Next
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </CustomCard>
        </div>
    );
};

export default AIUserChats;
